import { TailgateGridTestJobResult } from '../hooks/useTailgateGridTestJob'
import { useTranslation } from 'react-i18next'

import {
  Badge,
  Box,
  Flex,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'

import {
  Box as DarkBox,
  Flex as DarkFlex,
  Table as DarkTable,
  TableBody as DarkTableBody,
  TableCell as DarkTableCell,
  TableHeader as DarkTableHeader,
  TableHeaderCell as DarkTableHeaderCell,
  TableRow as DarkTableRow,
  Text as DarkText,
  tokenPrefix,
  tokenVar,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { Button } from '@/components/ui'
import {
  TailgateScalingMode,
  TailgateVerdictType,
} from '@/graphql/generated/schemas'
import { formatDateTime } from '@/utils/formatDateTime'
import { tVar } from '@/utils/i18n'

import { GridModalHeader } from '../../components/GridModalHeader'
import { scrollbarStyle } from '../../styles/styles'
import { IncidentOptionI } from '../../types/types'
import { TailgateGridTestPlayer } from './TailgateGridTestPlayer'

interface TailgateGridTestSuccessCardIProps {
  colorScheme?: ColorScheme
  isEnabled: boolean
  isSaving: boolean
  job: TailgateGridTestJobResult
  onClose: () => void
  onSaveGrid?: () => void
  onSelectAnotherIncident: () => void
  selectedIncident: IncidentOptionI | null
}

export const TailgateGridTestSuccessCard = ({
  colorScheme,
  isEnabled,
  isSaving,
  job,
  onClose,
  onSaveGrid,
  onSelectAnotherIncident,
  selectedIncident,
}: TailgateGridTestSuccessCardIProps) => {
  const { t } = useTranslation()
  const detectedPeople = job.verdictPersonCount
  const isVerdictMatch = job.verdictType
    ? job.verdictType === TailgateVerdictType.Tl
    : undefined
  const resolutionWidth =
    job.resolutionUsedWidth ?? job.streamingResolutionWidth
  const resolutionHeight =
    job.resolutionUsedHeight ?? job.streamingResolutionHeight
  const resolution =
    resolutionWidth && resolutionHeight
      ? `${resolutionWidth}×${resolutionHeight}`
      : t('tailgate:grid_test_v2.unavailable')
  const evaluatorVersion =
    job.evaluatorVersion ?? t('tailgate:grid_test_v2.unavailable')
  const detectedPeopleLabel =
    detectedPeople ?? t('tailgate:grid_test_v2.unavailable')
  const simulatedVerdict =
    job.verdictType ?? t('tailgate:grid_test_v2.unavailable')
  const verdictResult =
    isVerdictMatch === undefined
      ? t('tailgate:grid_test_v2.unavailable')
      : isVerdictMatch
      ? t('tailgate:results_step.match')
      : t('tailgate:grid_test_v2.changed')
  const badgeCount = job.verdictBadgeCount ?? job.badgeCount

  const player = (
    <TailgateGridTestPlayer
      colorScheme={colorScheme}
      incidentId={selectedIncident?.id}
      isEnabled={isEnabled}
      job={job}
    />
  )

  if (colorScheme) {
    const tokenPathPrefix = tokenPrefix(colorScheme)
    const borderColor = tokenVar(`colors.${tokenPathPrefix}border.default`)
    const secondaryText = tokenVar(`colors.${tokenPathPrefix}text.secondary`)
    const rowBackground = tokenVar(`colors.${tokenPathPrefix}bg.table-row-alt`)

    return (
      <DarkFlex direction='column' gap='20px'>
        {player}

        <DarkBox>
          <DarkText fontSize='16px' fontWeight='bold' mb='8px'>
            {t('tailgate:grid_test_v2.verdict_title')}
          </DarkText>
          <DarkTable colorScheme={colorScheme}>
            <DarkTableHeader>
              <DarkTableHeaderCell flexGrow={1}>
                {t('tailgate:grid_test_v2.production_verdict')}
              </DarkTableHeaderCell>
              <DarkTableHeaderCell flexGrow={1}>
                {t('tailgate:grid_test_v2.simulated_verdict')}
              </DarkTableHeaderCell>
              <DarkTableHeaderCell flexGrow={1}>
                {t('tailgate:results_step.table_result')}
              </DarkTableHeaderCell>
            </DarkTableHeader>
            <DarkTableBody>
              <DarkTableRow>
                <DarkTableCell flexGrow={1}>TL</DarkTableCell>
                <DarkTableCell flexGrow={1}>{simulatedVerdict}</DarkTableCell>
                <DarkTableCell flexGrow={1}>{verdictResult}</DarkTableCell>
              </DarkTableRow>
            </DarkTableBody>
          </DarkTable>
          <DarkText color={secondaryText} fontSize='13px' mt='8px'>
            {tVar(t, 'tailgate:grid_test_v2.verdict_inputs', {
              badgeCount,
              personCount: detectedPeopleLabel,
            })}
          </DarkText>
        </DarkBox>

        <DarkBox>
          <DarkText fontSize='16px' fontWeight='bold' mb='8px'>
            {t('tailgate:grid_test_v2.badge_events')}
          </DarkText>
          <DarkFlex
            direction='column'
            gap='6px'
            maxHeight='180px'
            overflowY='auto'
          >
            {job.badgeEvents.length === 0 ? (
              <DarkBox border='1px solid' borderColor={borderColor} p='12px'>
                <DarkText color={secondaryText} fontSize='14px'>
                  {t('tailgate:grid_test_v2.no_badge_events')}
                </DarkText>
              </DarkBox>
            ) : (
              job.badgeEvents.map((event, index) => (
                <DarkBox
                  bg={index % 2 === 1 ? rowBackground : undefined}
                  border='1px solid'
                  borderColor={borderColor}
                  key={event.id}
                  p='12px'
                >
                  <DarkText fontSize='14px'>
                    {event.badgeNumber ??
                      t('tailgate:grid_test_v2.unknown_badge')}
                    {' · '}
                    {event.cardHolderName ??
                      t('tailgate:grid_test_v2.unknown_cardholder')}
                    {' · '}
                    {formatDateTime(event.detectedAt)}
                  </DarkText>
                </DarkBox>
              ))
            )}
          </DarkFlex>
          <DarkText color={secondaryText} fontSize='13px' mt='8px'>
            {job.badgeCountCaveat}
          </DarkText>
        </DarkBox>

        <DarkBox border='1px solid' borderColor={borderColor} p='12px'>
          <DarkText fontSize='14px'>
            {tVar(t, 'tailgate:grid_test_v2.evaluator_stamp', {
              resolution,
              version: evaluatorVersion,
            })}
          </DarkText>
          {job.scalingMode === TailgateScalingMode.DecodedFallback && (
            <DarkText color={secondaryText} fontSize='13px' mt='6px'>
              {t('tailgate:grid_test_v2.decoded_fallback_caveat')}
            </DarkText>
          )}
        </DarkBox>
      </DarkFlex>
    )
  }

  const resultBadge = (label: string, isMatch?: boolean) => (
    <Badge
      colorScheme={isMatch === undefined ? 'gray' : isMatch ? 'green' : 'red'}
    >
      {label}
    </Badge>
  )

  const content = (
    <Flex direction='column' gap={5}>
      {player}

      <Box>
        <Text fontSize='md' fontWeight='bold' mb={2}>
          {t('tailgate:grid_test_v2.verdict_title')}
        </Text>
        <Table size='sm' sx={{ tableLayout: 'fixed' }}>
          <Thead>
            <Tr>
              <Th>{t('tailgate:grid_test_v2.production_verdict')}</Th>
              <Th>{t('tailgate:grid_test_v2.simulated_verdict')}</Th>
              <Th>{t('tailgate:results_step.table_result')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>TL</Td>
              <Td>{simulatedVerdict}</Td>
              <Td>{resultBadge(verdictResult, isVerdictMatch)}</Td>
            </Tr>
          </Tbody>
        </Table>
        <Text color='text.secondary.light' fontSize='sm' mt={2}>
          {tVar(t, 'tailgate:grid_test_v2.verdict_inputs', {
            badgeCount,
            personCount: detectedPeopleLabel,
          })}
        </Text>
      </Box>

      <Box>
        <Text fontSize='md' fontWeight='bold' mb={2}>
          {t('tailgate:grid_test_v2.badge_events')}
        </Text>
        <Flex
          direction='column'
          gap={1.5}
          maxH='180px'
          overflowY='auto'
          sx={scrollbarStyle}
        >
          {job.badgeEvents.length === 0 ? (
            <Box border='1px solid' borderColor='border.default.light' p={3}>
              <Text color='text.secondary.light' fontSize='sm'>
                {t('tailgate:grid_test_v2.no_badge_events')}
              </Text>
            </Box>
          ) : (
            job.badgeEvents.map((event) => (
              <Box
                border='1px solid'
                borderColor='border.default.light'
                key={event.id}
                p={3}
              >
                <Text fontSize='sm'>
                  {event.badgeNumber ??
                    t('tailgate:grid_test_v2.unknown_badge')}
                  {' · '}
                  {event.cardHolderName ??
                    t('tailgate:grid_test_v2.unknown_cardholder')}
                  {' · '}
                  {formatDateTime(event.detectedAt)}
                </Text>
              </Box>
            ))
          )}
        </Flex>
        <Text color='text.secondary.light' fontSize='sm' mt={2}>
          {job.badgeCountCaveat}
        </Text>
      </Box>

      <Box border='1px solid' borderColor='border.default.light' p={3}>
        <Text fontSize='sm'>
          {tVar(t, 'tailgate:grid_test_v2.evaluator_stamp', {
            resolution,
            version: evaluatorVersion,
          })}
        </Text>
        {job.scalingMode === TailgateScalingMode.DecodedFallback && (
          <Text color='text.secondary.light' fontSize='sm' mt={1.5}>
            {t('tailgate:grid_test_v2.decoded_fallback_caveat')}
          </Text>
        )}
      </Box>
    </Flex>
  )

  return (
    <>
      <ModalHeader p={0} w='100%'>
        <GridModalHeader
          subtitle={tVar(t, 'tailgate:results_step.subtitle', {
            incidentId: selectedIncident?.incidentId ?? '',
          })}
          title={t('tailgate:results_step.title')}
        />
      </ModalHeader>
      <ModalBody p={0}>{content}</ModalBody>
      <ModalFooter mt={6} p={0}>
        <Flex direction='row' gap={3} justifyContent='flex-end'>
          <Button mr={0} onClick={onClose} variant='secondary'>
            {t('common:cancel')}
          </Button>
          <Button mr={0} onClick={onSelectAnotherIncident} variant='secondary'>
            {t('tailgate:results_step.select_another_incident')}
          </Button>
          <Button isDisabled={isSaving} onClick={onSaveGrid}>
            {t('tailgate:results_step.save_new_grid')}
          </Button>
        </Flex>
      </ModalFooter>
    </>
  )
}
